import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, catchError, firstValueFrom, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { FLASH_CALL_ACCESS_DISABLED, FlashCallAccess } from '../models/flash-call.model';
import {
  AccountUser,
  AdminUserUpdateResult,
  AdminVatValidationResult,
  AuthUser,
  BasicUser,
  BasicUserCollection,
  LoginCredentials,
  MfaCredentials,
  MfaSetup,
  RegistrationByInviteModel,
  RegistrationCompanyBillingModel,
  RegistrationCompanyBillingResponseModel,
  RegistrationMainInfoModel,
  RegistrationMainInfoResponseModel,
  RegistrationModel,
  Role,
  SubaccountInviteResponseModel,
  TrustedDeviceCollection,
  TrustedDeviceRequestParams,
  UpdatePasswordModel,
  UserAdmin,
  UserAdminCollection,
  UserAgreementSettingsUpdateModel,
  UserFormData,
  UserSearchEvent,
  ZohoCustomerSettings,
  ZohoCustomerSettingsUpdate,
  formToCreateModel,
  formToUpdateModel,
  minBalanceFromMilliEuros,
  minBalanceToMilliEuros,
} from '../models/user.model';
import { InvoiceRecipientsPayload } from '../../users/invoice-recipients';
import { RestUtils } from './rest-utils';
import { Validators as Vld } from './validation.service';

@Injectable()
export class UsersService {
  static TOTP_LENGTH = 6;

  utils = new RestUtils();

  headers = new HttpHeaders();

  sourceItems = [
    { id: '', label: '' },
    { id: 'NETWORKING', label: 'Networking' },
    { id: 'ADS', label: 'ADS' },
    { id: 'GOOGLE', label: 'Google' },
    { id: 'CONFERENCE', label: 'Conference' },
    { id: 'REFERRAL', label: 'Referral' },
    { id: 'OTHER', label: 'Other' },
  ];

  authUser: AuthUser = null;
  isAuth = false;

  authUpdates = new Subject<AuthUser>();
  balanceUpdates = new BehaviorSubject<number>(0);
  private balanceValue = 0;

  private actionSubject: Subject<UserActionData> = new Subject<UserActionData>();
  action$ = this.actionSubject.asObservable();

  private isMoAvailableForUserSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private billingAvailableSubject$: BehaviorSubject<boolean | null> = new BehaviorSubject<boolean | null>(null);

  constructor(private http: HttpClient) {
    this.headers = this.headers.set('Content-Type', 'application/json');

    setInterval(() => {
      this.updateBalance();
    }, 15 * 1000);
  }

  announceAction(action: UserActionData) {
    this.actionSubject.next(action);
  }

  static getValidatorForTestTtlMin() {
    return Vld.compose([Vld.required, Vld.min(1), Vld.max(720), Vld.integer()]);
  }

  public setMoAvailableForUser(isMoAvailable: boolean) {
    this.isMoAvailableForUserSubject$.next(isMoAvailable);
  }

  public getMoAvailableForUser(): Observable<boolean> {
    return this.isMoAvailableForUserSubject$.asObservable();
  }

  public getBillingAvailableForUser(): Observable<boolean | null> {
    return this.billingAvailableSubject$.asObservable();
  }

  public setBillingAvailableForUser(isBillingAvailable: boolean | null) {
    this.billingAvailableSubject$.next(isBillingAvailable);
  }

  isMoAvailableForUser(): Observable<boolean> {
    const url = this.utils.buildUrl('ROLE/usr/users/self/mo/enabled');
    const options = this.utils.getHttpHeaderOptions(this.headers, false);
    return this.http.get<boolean>(url, options);
  }

  isVoiceAvailableForUser(): Observable<boolean> {
    const url = this.utils.buildUrl('ROLE/usr/users/self/voice/enabled');
    const options = this.utils.getHttpHeaderOptions(this.headers, false);
    return this.http.get<boolean>(url, options);
  }

  getFlashCallAccess(): Observable<FlashCallAccess> {
    const url = this.utils.buildUrl('ROLE/usr/users/self/flash-call/enabled');
    const options = this.utils.getHttpHeaderOptions(this.headers, false);
    return this.http.get<FlashCallAccess>(url, options).pipe(
      map(access => {
        const isValid = access?.enabled === true && access.ttlSeconds > 0;
        const featureEnabled = access?.featureEnabled === true;
        return isValid ? { enabled: true, featureEnabled, ttlSeconds: access.ttlSeconds } : { ...FLASH_CALL_ACCESS_DISABLED, featureEnabled };
      }),
      catchError(() => of(FLASH_CALL_ACCESS_DISABLED))
    );
  }

  isBillingAvailableForUser(): Observable<boolean> {
    const url = this.utils.buildUrl('ROLE/usr/users/self/billing/enabled');
    const options = this.utils.getHttpHeaderOptions(this.headers, false);
    return this.http.get<boolean>(url, options).pipe(
      tap(isBillingAvailable => {
        this.setBillingAvailableForUser(isBillingAvailable);
      })
    );
  }

  private updateBalance() {
    if (this.isAuth) {
      this.getBalance().subscribe(currentBalance => {
        if (currentBalance !== this.balanceValue) {
          this.balanceValue = currentBalance;
          this.balanceUpdates.next(currentBalance);
        }
      });
    }
  }

  getBalance(): Observable<number> {
    const url = this.utils.buildUrl('ROLE/usr/users/self/balance');
    const options = this.utils.getHttpHeaderOptions(this.headers, false);

    return this.http.get<{ balanceEuro: number }>(url, options).pipe(
      map(_ => {
        return _.balanceEuro;
      })
    );
  }

  getAuthUser(): Promise<AuthUser> {
    return new Promise((resolve, reject) => {
      if (this.authUser !== null) {
        const user = Object.assign({}, this.authUser);
        resolve(user);
        return;
      }
      this.i().subscribe(
        () => {
          resolve(this.authUser);
        },
        e => {
          this.isAuth = false;
          RestUtils._role = Role.GUEST;
          reject(e);
        }
      );
    });
  }

  init(): Promise<boolean> {
    return new Promise(resolve => {
      this.getAuthUser()
        .then(user => {
          this.authUpdates = new BehaviorSubject(user);
          resolve(true);
        })
        .catch(() => {
          this.isAuth = false;
          RestUtils._role = Role.GUEST;
          resolve(true);
        });
    });
  }

  clearAuthUser() {
    this.isAuth = false;
    this.authUser = null;
    RestUtils._role = Role.GUEST;
  }

  i() {
    const url = this.utils.buildUrl('usr/users/self');
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.get<AuthUser>(url, options).pipe(
      map(_ => {
        const data = _;
        data.testTtlMin = data.testTtl / 60;
        if (data.minBalance != null) {
          data.minBalance = minBalanceFromMilliEuros(data.minBalance);
        }
        this.isAuth = true;
        if (!this.authUser || this.authUser.id !== data.id) {
          this.authUser = data;
          RestUtils._role = data.role;
          this.updateBalance();
          this.authUpdates.next(this.authUser);
        } else {
          const companyDetailsVerifiedChanged = this.authUser.companyDetailsVerified !== data.companyDetailsVerified;

          this.authUser.email = data.email;
          this.authUser.defaultLntTemplate = data.defaultLntTemplate;
          this.authUser.enabled = data.enabled;
          this.authUser.showAllResults = data.showAllResults;
          this.authUser.testRetriesEnabled = data.testRetriesEnabled;
          this.authUser.testTtl = data.testTtl;
          this.authUser.testTtlMin = data.testTtlMin;
          this.authUser.minBalance = data.minBalance;
          this.authUser.vatPercentage = data.vatPercentage;
          this.authUser.companyDetailsVerified = data.companyDetailsVerified;
          this.authUser.isOnboarded = data.isOnboarded;

          if (companyDetailsVerifiedChanged) {
            this.authUpdates.next(this.authUser);
          }
        }

        return data;
      })
    );
  }

  basicList(limit = 10, page = 1, search: string, roles: string[], paymentType = '') {
    const queryParams = {
      page: page - 1,
      size: limit,
      search: search ? search : '',
      roles: roles.length ? roles.join(',') : '',
      paymentType: paymentType,
    };

    const url = this.utils.buildUrl('ROLE/usr/users/basic-list', queryParams);
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.get<BasicUserCollection>(url, options);
  }

  basicListByIds(ids: number[]) {
    const url = this.utils.buildUrl('ROLE/usr/users/basic-list/by-ids', {
      ids: ids.join(','),
    });
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.get<BasicUser[]>(url, options);
  }

  all(params: UsersRequestParams, searchData: UserSearchEvent = null): Observable<UserAdminCollection> {
    const queryParams = {
      page: params.page,
      size: params.size,
      sort: params.sort.length ? params.sort : [],
      search: params.search ? params.search : '',
    };

    let url = this.utils.buildUrl('ROLE/usr/users/search', queryParams);
    const options = this.utils.getHttpHeaderOptions(this.headers);
    if (searchData) {
      return this.http.post<UserAdminCollection>(url, searchData, options);
    }
    if (Role.ADMIN !== this.authUser.role) {
      url = this.utils.buildUrl('ROLE/usr/users', queryParams);
    }
    return this.http.get<UserAdminCollection>(url, options);
  }

  one(id: number) {
    const url = this.utils.buildUrl('ROLE/usr/users/' + id);
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.get<UserFormData>(url, options).pipe(
      map(_ => {
        const user = _;
        if (user.testTtl) {
          user.testTtl = user.testTtl / 60;
        }
        if (user.minBalance != null) {
          user.minBalance = minBalanceFromMilliEuros(user.minBalance);
        }
        return user;
      })
    );
  }

  updateEnable(userId: number, enable: boolean) {
    const url = this.utils.buildUrl('ROLE/usr/users/' + userId + '/enable', { enabled: enable ? 'true' : 'false' });
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.put(url, {}, options);
  }

  updateShowAllResults(userId: number, showAllResults: boolean) {
    const url = this.utils.buildUrl('ROLE/usr/users/' + userId + '/show-all-results', { showAllResults: showAllResults ? 'true' : 'false' });
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.put(url, {}, options);
  }

  updateMoEnabled(userId: number, moEnabled: boolean) {
    const url = this.utils.buildUrl('ROLE/usr/users/' + userId + '/mo-enabled', { enabled: moEnabled ? 'true' : 'false' });
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.put(url, {}, options);
  }

  updateMoAllowedNumbersEnabled(userId: number, enabled: boolean) {
    const url = this.utils.buildUrl('ROLE/usr/users/' + userId + '/mo-allowed-numbers-enabled', { enabled: enabled ? 'true' : 'false' });
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.put(url, {}, options);
  }

  updateVoiceEnabled(userId: number, voiceEnabled: boolean) {
    const url = this.utils.buildUrl('ROLE/usr/users/' + userId + '/voice-enabled', { enabled: voiceEnabled ? 'true' : 'false' });
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.put(url, {}, options);
  }

  toggleSubaccountOrDeputy(userId: number, role: string) {
    const roleChangeTo = role === Role.DEPUTY ? Role.SUB : Role.DEPUTY;
    const url = this.utils.buildUrl('ROLE/usr/users/' + userId + '/role/' + roleChangeTo, {});
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post(url, {}, options);
  }

  delete(id: number) {
    const url = this.utils.buildUrl('ROLE/usr/users/' + id);
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.delete(url, options);
  }

  create() {
    return {
      id: null,
      enabled: true,
      userRole: 'mainaccount',
      paymentType: 'PREPAID',
      testRetriesEnabled: true,
      emailVerified: false,
      sendVerificationEmail: true,
      rankingIndex: 3,
      testTtl: 60,
      vatExempt: false,
      minBalance: 1,
      phone: '',
    };
  }

  save(data: any): Observable<any> {
    const newData = Object.assign({}, data);

    if (typeof newData.testTtl !== 'undefined') {
      newData.testTtl = parseInt(newData.testTtl) * 60;
    }

    const id = newData.id;
    const url = this.utils.buildUrl('ROLE/usr/users/' + id);

    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.put(url, newData, options);
  }

  updateByAdmin(data: UserFormData): Observable<AdminUserUpdateResult> {
    const id = parseInt(String(data.id));
    const url = this.utils.buildUrl('ROLE/usr/users/' + id);
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.put<AdminUserUpdateResult>(url, formToUpdateModel(data), options);
  }

  createByAdmin(data: UserFormData) {
    const url = this.utils.buildUrl('ROLE/usr/users');
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.post(url, formToCreateModel(data), options);
  }

  selfUpdate(user: AccountUser) {
    const data = Object.assign({}, user);
    if (data.testTtl) {
      data.testTtl = parseInt(String(data.testTtl)) * 60;
    }
    if (data.minBalance != null) {
      data.minBalance = minBalanceToMilliEuros(data.minBalance);
    }

    const url = this.utils.buildUrl('usr/users/self');
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.put(url, data, options).pipe(
      map(_ => {
        this.authUser = null;
        return _;
      })
    );
  }

  getSelfInvoiceRecipients(): Observable<InvoiceRecipientsPayload> {
    const url = this.utils.buildUrl('ROLE/usr/users/self/invoice-recipients');
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.get<InvoiceRecipientsPayload>(url, options);
  }

  updateSelfInvoiceRecipients(payload: InvoiceRecipientsPayload): Observable<InvoiceRecipientsPayload> {
    const url = this.utils.buildUrl('ROLE/usr/users/self/invoice-recipients');
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.put<InvoiceRecipientsPayload>(url, payload, options);
  }

  getInvoiceRecipientsByAdmin(userId: number): Observable<InvoiceRecipientsPayload> {
    const url = this.utils.buildUrl(`admin/usr/users/${userId}/invoice-recipients`);
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.get<InvoiceRecipientsPayload>(url, options);
  }

  updateInvoiceRecipientsByAdmin(userId: number, payload: InvoiceRecipientsPayload): Observable<InvoiceRecipientsPayload> {
    const url = this.utils.buildUrl(`admin/usr/users/${userId}/invoice-recipients`);
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.put<InvoiceRecipientsPayload>(url, payload, options);
  }

  getZohoCustomerSettings(userId: number): Observable<ZohoCustomerSettings> {
    const url = this.utils.buildUrl(`admin/usr/users/${userId}/zoho-settings`);
    return this.http.get<ZohoCustomerSettings>(url, this.utils.getHttpHeaderOptions(this.headers));
  }

  updateZohoCustomerSettings(userId: number, payload: ZohoCustomerSettingsUpdate): Observable<ZohoCustomerSettings> {
    const url = this.utils.buildUrl(`admin/usr/users/${userId}/zoho-settings`);
    return this.http.put<ZohoCustomerSettings>(url, payload, this.utils.getHttpHeaderOptions(this.headers));
  }

  validateVatByAdmin(userId: number): Observable<AdminVatValidationResult> {
    const url = this.utils.buildUrl(`admin/usr/users/${userId}/validate-vat`);
    return this.http.post<AdminVatValidationResult>(url, null, this.utils.getHttpHeaderOptions(this.headers));
  }

  resetPassword(userId: number, passwordModel: UpdatePasswordModel) {
    const url = this.utils.buildUrl('guest/usr/users/' + userId + '/reset-password');
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.put(url, passwordModel, options);
  }

  updateClicktermAgreementSettings(userId: number, settings: UserAgreementSettingsUpdateModel) {
    const url = this.utils.buildUrl('admin/usr/users/' + userId + '/agreement-settings');
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.put(url, settings, options);
  }

  updatePassword(userId: number, passwordModel, guestMode = false) {
    const role = guestMode ? 'guest' : 'ROLE';
    const url = this.utils.buildUrl(role + '/usr/users/' + userId + '/password');
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.put(url, passwordModel, options);
  }

  updateEmail(newEmail, guestMode = false) {
    const role = guestMode ? 'guest' : 'ROLE';
    const url = this.utils.buildUrl(role + '/usr/change-email', { email: newEmail });
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.post(url, newEmail, options);
  }

  ping() {
    if (this.isAuth) {
      const url = this.utils.buildUrl('ROLE/usr/users/self/last-active');
      const options = this.utils.getHttpHeaderOptions(this.headers);

      this.http.post(url, null, options).subscribe(
        r => {},
        error => {}
      );
    }
  }

  getTypes() {
    return [
      {
        id: 'PREPAID',
        title: 'Prepayment',
      },
      {
        id: 'POSTPAID',
        title: 'Postpayment',
      },
    ];
  }

  getCreateRoles() {
    return [
      /*{
       id: 'admin',
       title: 'Admin'
       },*/
      {
        id: 'mainaccount',
        title: 'Main account',
      },
      {
        id: 'deputy',
        title: 'Deputy account',
      },
      {
        id: 'subaccount',
        title: 'Subaccount',
      },
    ];
  }

  can(roleName: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const role = RestUtils.getRole();
      if (roleName === 'client') {
        resolve(role === Role.MAIN || role === Role.DEPUTY || role === Role.SUB);
        return;
      }
      resolve(role === roleName);
    });
  }

  impersonate(userId: number) {
    const url = this.utils.buildUrl('admin/usr/users/' + userId + '/impersonate');
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post(url, null, options).pipe(
      map(_ => {
        this.clearAuthUser();
      })
    );
  }

  logout(url: string[], impersonateUrl: string[]): Promise<string[]> {
    const hasPrevToken = this.isAuth ? this.authUser.impersonated : false;
    const urlRequest = this.utils.buildUrl('ROLE/usr/logout');
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return new Promise(resolve => {
      this.http.post(urlRequest, null, options).subscribe(
        () => {
          this.clearAuthUser();
          resolve(hasPrevToken ? impersonateUrl : url);
        },
        () => {
          this.clearAuthUser();
          resolve(url);
        }
      );
    });
  }

  login(credentials: LoginCredentials) {
    const url = this.utils.buildUrl('guest/usr/login');
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post(url, credentials, options).pipe(
      map(_ => {
        this.clearAuthUser();
        return _;
      })
    );
  }

  registration(data: RegistrationModel) {
    const url = this.utils.buildUrl('guest/usr/users/register');
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post(url, data, options);
  }

  registrationMainInfo(data: RegistrationMainInfoModel): Observable<RegistrationMainInfoResponseModel> {
    const url = this.utils.buildUrl('guest/usr/users/register/main-info');
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post<RegistrationMainInfoResponseModel>(url, data, options);
  }

  validateRegistration(data: RegistrationModel) {
    const url = this.utils.buildUrl('guest/usr/users/register/validate');
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post(url, data, options);
  }

  validateRegistrationMainInfo(data: RegistrationMainInfoModel) {
    const url = this.utils.buildUrl('guest/usr/users/register/main-info/validate');
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post(url, data, options);
  }

  submitRegistrationCompanyBilling(data: RegistrationCompanyBillingModel): Observable<RegistrationCompanyBillingResponseModel> {
    const url = this.utils.buildUrl('guest/usr/users/register/company-billing');
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post<RegistrationCompanyBillingResponseModel>(url, data, options);
  }

  registrationByInvite(data: RegistrationByInviteModel, invite: string, queryString?: string): Observable<SubaccountInviteResponseModel> {
    const url = this.utils.buildUrl('guest/usr/users/register/by-invite', { invite, queryString });
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post<SubaccountInviteResponseModel>(url, data, options);
  }

  recovery(username: string, resend = false) {
    const url = this.utils.buildUrl('guest/usr/request-password-reset', { username: username, resend: resend });
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post(url, null, options);
  }

  validateRecoveryToken(userId, token) {
    const url = this.utils.buildUrl('guest/usr/validate-password-reset', { userId: userId, token: token });
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.get(url, options);
  }

  verifyToken(token: string, userId: number) {
    const url = this.utils.buildUrl('guest/usr/verify-email', { userId: userId, token: token });
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.get(url, options);
  }

  resendVerificationEmail(userId: number) {
    const url = this.utils.buildUrl(`admin/usr/users/${userId}/resend-verification-by-admin`);
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post(url, null, options);
  }

  verifyCompanyDetails(userId: number) {
    const url = this.utils.buildUrl(`admin/usr/users/${userId}/verify-company-details`);
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post(url, null, options);
  }

  convertToSubaccount(userId: number, parentId: number) {
    const url = this.utils.buildUrl(`admin/usr/users/${userId}/role/subaccount`, { parentId: parentId });
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post(url, null, options);
  }

  saveDefaultTemplate(templateId: number) {
    const queryParams = { templateId: templateId };
    if (!templateId) {
      delete queryParams.templateId;
    }
    const url = this.utils.buildUrl('ROLE/usr/users/self/defaultLntTemplate', queryParams);
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.put(url, {}, options);
  }

  getUserDetailsFromEmail(parentEmail: string): Observable<UserAdmin> {
    const url = this.utils.buildUrl('ROLE/usr/users', {
      size: 1,
      page: 0,
      search: parentEmail,
    });
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.get<UserAdminCollection>(url, options).pipe(
      map(response => {
        return response.content.length ? response.content[0] : null;
      })
    );
  }

  mfaRecovery(credentials: MfaCredentials): Observable<any> {
    const url = this.utils.buildUrl('guest/usr/mfa/recovery');
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post(url, credentials, options);
  }

  mfaSetup(): Observable<MfaSetup> {
    const url = this.utils.buildUrl('ROLE/usr/users/self/mfa/totp-setup');
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post<MfaSetup>(url, {}, options);
  }

  mfaEnable(mfaCode: string): Observable<any> {
    const url = this.utils.buildUrl('ROLE/usr/users/self/mfa-enable');
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post(url, { mfaCode: mfaCode }, options);
  }

  mfaDisable(mfaCode: string): Observable<any> {
    const url = this.utils.buildUrl('ROLE/usr/users/self/mfa-disable');
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post(url, { mfaCode: mfaCode }, options);
  }

  mfaDisableByAdmin(userId: number) {
    const url = this.utils.buildUrl(`admin/usr/users/${userId}/mfa-disable-by-admin`);
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post(url, null, options);
  }

  mfaGenerateCodes() {
    const url = this.utils.buildUrl('ROLE/usr/users/self/mfa/generate-recovery-codes');
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post<string[]>(url, {}, options);
  }

  resendEmailCode(login: string, resend = false): Observable<any> {
    const url = this.utils.buildUrl('/guest/usr/request-otp-for-user', { username: login, resend: resend });
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post(url, null, options);
  }

  allDevices(requestParams: TrustedDeviceRequestParams): Observable<TrustedDeviceCollection> {
    const url = this.utils.buildUrl('ROLE/usr/trusted-device', {
      page: requestParams.page,
      size: requestParams.size,
      sort: requestParams.sort.length ? requestParams.sort : [],
    });
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.get<TrustedDeviceCollection>(url, options);
  }

  deleteDevice(deviceId: number) {
    const url = this.utils.buildUrl('ROLE/usr/trusted-device/' + deviceId);
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.delete<void>(url, options);
  }

  checkPasswordValidity(password: string): Observable<string[] | null> {
    const url = this.utils.buildUrl('guest/usr/users/validate-password');
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post(url, { password }, options).pipe(
      catchError((e: HttpErrorResponse) => {
        if (e.status === 400) {
          const errors = e.statusText.split('-').filter(_ => _.trim() !== 'Invalid password:');
          if (!errors.length) {
            errors.push('Invalid password');
          }
          return of({ passwordBackend: errors });
        }
        return of(null);
      }),
      map((res: string[] | null) => (res ? res : null))
    );
  }

  otpValidate(code: string) {
    const url = this.utils.buildUrl('ROLE/usr/otp/validate');
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post<void>(url, { code: code, otpChannel: 'EMAIL' }, options);
  }

  otpResend(action: string, resend = false) {
    const url = this.utils.buildUrl('ROLE/usr/otp/generate', { resend: resend });
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post<void>(url, { action: action, otpChannel: 'EMAIL' }, options);
  }

  validateInvite(invite: string): Observable<boolean> {
    const url = this.utils.buildUrl('guest/usr/validate-invite', { invite: invite });
    const options = this.utils.getHttpHeaderOptions(this.headers, false);
    return this.http.get<boolean>(url, options);
  }

  getCallbackUrlLiveTesting() {
    const url = this.utils.buildUrl('ROLE/usr/users/self/callback-url-live');
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.get(url, { ...options, responseType: 'text' }).pipe(map(r => (r ? r : '')));
  }

  updateCallbackUrlLiveTesting(callbackUrl: string) {
    let url = this.utils.buildUrl('ROLE/usr/users/self/callback-url-live', { callbackUrl: callbackUrl });

    if (!callbackUrl && !url.includes('?callbackUrl=')) {
      url += '?callbackUrl=';
    }

    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.put<void>(url, null, options);
  }

  getGeneralCallbackUrlLiveTesting() {
    const url = this.utils.buildUrl('ROLE/usr/users/self/general-callback-url-live');
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.get(url, { ...options, responseType: 'text' }).pipe(map(r => (r ? r : '')));
  }

  updateGeneralUpdateCallbackUrl(callbackUrl: string) {
    let url = this.utils.buildUrl('ROLE/usr/users/self/general-callback-url-live', { callbackUrl: callbackUrl });
    if (!callbackUrl && !url.includes('?callbackUrl=')) {
      url += '?callbackUrl=';
    }
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.put<void>(url, null, options);
  }

  getParentAccountDetails(): Observable<AccountUser> {
    const url = this.utils.buildUrl('ROLE/usr/users/self/get-parent-account-details');
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.get<AccountUser>(url, options);
  }

  updateComment(userId: number, comment: string) {
    const url = this.utils.buildUrl('ROLE/usr/users/' + userId + '/comment');
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.put(url, comment, options);
  }

  adjustBalanceByAdmin(userId: number, request: AdminBalanceAdjustmentRequest): Observable<void> {
    const url = this.utils.buildUrl(`admin/usr/users/${userId}/balance-adjustment`);
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post<void>(url, request, options);
  }

  loadExternalUserId(): Promise<string | null> {
    return firstValueFrom(
      this.getExternalUserId().pipe(
        map(_ => _?.externalUserId ?? null),
        catchError(() => of(null))
      )
    );
  }

  getExternalUserId(): Observable<{ externalUserId: string }> {
    const url = this.utils.buildUrl('ROLE/usr/users/external-id', { provider: 'clickterm' });
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.get<{ externalUserId: string }>(url, options);
  }
}

export class UsersRequestParams {
  size = 20;
  page = 1;
  search: string;

  sort: string[] = [];

  setSort(propertyName: string, direction: string) {
    this.sort.push(propertyName + (direction === 'desc' ? ',desc' : ''));
  }

  removeSort(propertyName: string) {
    this.sort = this.sort.filter(_ => _.indexOf(propertyName) === -1);
  }

  resetSort() {
    this.sort = [];
  }
}

export interface UserActionData {
  name: string;
  row: UserAdmin;
  column: string;
}

export interface AdminBalanceAdjustmentRequest {
  operation: 'SET_TO_ZERO' | 'ADJUST';
  amountEuro?: number;
}
